import { Injectable } from '@angular/core';
import type { ParcelModule } from '../interfaces/single-spa-parcel.interface';

const BANNER_HOST_ALLOWLIST = [
  'qa-web-banner-ic-transversal.iconstruye.cl',
  'qa-web-banner-ic-transversal.agilice.cl',
  'qa-web-banner-ic-transversal-redmat.iconstruye.cl',
  'web-banner-ic-transversal.iconstruye.com',
  'web-banner-ic-transversal.agilice.com',
  'web-banner-ic-transversal-redmat.iconstruye.com'
] as const;

const LOAD_TIMEOUT_MS = 15_000;

/**
 * Descarga validada del bundle ESM del banner MF (whitelist de host + HTTPS + timeout).
 * OWASP A01/A05/A10/A09 — sin ejecutar entrada no permitida ni bloquear el menú en fallo silencioso del caller.
 */
@Injectable({
  providedIn: 'root'
})
export class BannerTransversalLoaderService {
  private cachedHref: string | null = null;
  private cachedModulePromise: Promise<ParcelModule> | null = null;

  /** Expuesto para tests o diagnóstico; no usar como fuente mutable en runtime */
  readonly allowedHosts: readonly string[] = BANNER_HOST_ALLOWLIST;

  async loadBannerModule(url: string): Promise<ParcelModule> {
    const safeHref = this.validateBannerUrl(url);

    if (this.cachedModulePromise !== null && this.cachedHref === safeHref) {
      return this.cachedModulePromise;
    }

    const correlationId = this.createCorrelationId();
    const loadPromise = this.loadBannerModuleUnsafe(safeHref, correlationId);
    this.cachedHref = safeHref;
    this.cachedModulePromise = loadPromise.catch((err: unknown) => {
      if (this.cachedHref === safeHref) {
        this.cachedHref = null;
        this.cachedModulePromise = null;
      }
      throw err;
    });

    return this.cachedModulePromise;
  }

  private validateBannerUrl(url: string): string {
    const trimmed = (url ?? '').trim();
    if (!trimmed) {
      throw new Error('Banner URL empty');
    }

    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      throw new Error('Banner URL parse failed');
    }

    if (parsed.protocol !== 'https:') {
      throw new Error('Banner URL must use https');
    }

    if (!this.allowedHosts.includes(parsed.hostname)) {
      throw new Error('Banner host not allowed');
    }

    if (parsed.hash.length > 0) {
      throw new Error('Banner URL must not include hash');
    }

    return parsed.href;
  }

  private async loadBannerModuleUnsafe(url: string, correlationId: string): Promise<ParcelModule> {
    const timeoutReject = (): Promise<never> =>
      new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`bannerTransversal_TIMEOUT_${LOAD_TIMEOUT_MS}ms`));
        }, LOAD_TIMEOUT_MS);
      });

    try {
      const mod = await Promise.race([this.importRemoteModule(url), timeoutReject()]);

      const parcel = this.resolveParcelModule(mod);
      return parcel;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'unknown_error';
      this.logStructured('bannerTransversalLoadFailed', correlationId, { urlHostname: safelyHostname(url), message });
      throw err;
    }
  }

  /**
   * En shells single-spa suele existir SystemJS; UMD/System resuelve mejor que import() nativo.
   */
  private importRemoteModule(url: string): Promise<unknown> {
    const system = (globalThis as { System?: { import?: (u: string) => Promise<unknown> } }).System;
    if (typeof system?.import === 'function') {
      return system.import(url);
    }
    return import(/* webpackIgnore: true */ url);
  }

  /** Busca lifecycles en el namespace raíz o en default (UMD / re-exports webpack). */
  private resolveParcelModule(candidate: unknown, depth = 0): ParcelModule {
    const found = this.findParcelModule(candidate, depth);
    if (found) {
      return found;
    }

    const exportKeys = this.describeModuleExports(candidate);
    throw new Error(
      `Parcel module missing bootstrap (exports: ${exportKeys}). ` +
        'El bundle del banner debe exportar bootstrap, mount y unmount (entrada single-spa, p. ej. main.single-spa.ts).'
    );
  }

  private findParcelModule(candidate: unknown, depth: number): ParcelModule | null {
    if (!candidate || typeof candidate !== 'object' || depth > 3) {
      return null;
    }

    if (this.hasParcelLifecycles(candidate)) {
      return candidate as ParcelModule;
    }

    const nested = (candidate as { default?: unknown }).default;
    if (nested !== undefined && nested !== candidate) {
      return this.findParcelModule(nested, depth + 1);
    }

    return null;
  }

  private hasParcelLifecycles(candidate: object): boolean {
    const m = candidate as ParcelModule;
    return (
      this.isLifecycle(m.bootstrap) &&
      this.isLifecycle(m.mount) &&
      this.isLifecycle(m.unmount)
    );
  }

  private isLifecycle(value: unknown): boolean {
    if (typeof value === 'function') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length > 0 && value.every((fn) => typeof fn === 'function');
    }
    return false;
  }

  private describeModuleExports(candidate: unknown): string {
    if (!candidate || typeof candidate !== 'object') {
      return '(not an object)';
    }
    const keys = Object.keys(candidate as object);
    return keys.length > 0 ? keys.slice(0, 12).join(', ') : '(empty)';
  }

  private createCorrelationId(): string {
    const timePrefix = Date.now().toString(36);
    try {
      const u = typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID?.();
      if (u) {
        return `${timePrefix}-${u}`;
      }
    } catch {
      /* fail closed to Math.random fallback */
    }
    return `${timePrefix}-${Math.random().toString(36).slice(2, 12)}`;
  }

  private logStructured(event: string, correlationId: string, extra: Record<string, string>): void {
    console.warn(JSON.stringify({ event, correlationId, timestamp: new Date().toISOString(), ...extra }));
  }
}

function safelyHostname(href: string): string {
  try {
    return new URL(href).hostname;
  } catch {
    return '(invalid)';
  }
}
